import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Award, ArrowLeft, Trophy, Clock, Star, 
  RotateCcw, Volume2, Check, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const SpellingBeeGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const inputRef = useRef(null);
  
  const [gameState, setGameState] = useState('ready');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userSpelling, setUserSpelling] = useState('');
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [showResult, setShowResult] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const words = [
    { word: 'because', hint: 'The reason for something' },
    { word: 'friend', hint: 'Someone you like to spend time with' },
    { word: 'beautiful', hint: 'Very pretty or attractive' },
    { word: 'different', hint: 'Not the same' },
    { word: 'important', hint: 'Has great value or significance' },
    { word: 'together', hint: 'With each other' },
    { word: 'thought', hint: 'An idea in your mind' },
    { word: 'through', hint: 'From one side to the other' },
    { word: 'believe', hint: 'To think something is true' },
    { word: 'surprise', hint: 'Something unexpected' },
    { word: 'favourite', hint: 'The one you like best' },
    { word: 'necessary', hint: 'Required or needed' },
    { word: 'separate', hint: 'To keep apart' },
    { word: 'Wednesday', hint: 'A day of the week' },
    { word: 'February', hint: 'A month of the year' },
  ];

  const currentWord = words[currentWordIndex];

  const speakWord = useCallback((wordToSpeak = null) => {
    // Cancel any ongoing speech first
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    
    const word = wordToSpeak || currentWord;
    if ('speechSynthesis' in window && word) {
      setIsPlaying(true);
      const utterance = new SpeechSynthesisUtterance(word.word);
      utterance.rate = 0.8;
      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = () => setIsPlaying(false);
      speechSynthesis.speak(utterance);
    }
  }, [currentWord]);

  const startGame = () => {
    // Cancel any previous speech
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    setGameState('playing');
    setCurrentWordIndex(0);
    setUserSpelling('');
    setScore(0);
    setCorrectCount(0);
    setShowResult(null);
    setTimeout(() => {
      speakWord(words[0]); // Pass the word directly
      inputRef.current?.focus();
    }, 500);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (gameState !== 'playing' || !userSpelling) return;
    
    const isCorrect = userSpelling.toLowerCase().trim() === currentWord.word.toLowerCase();
    
    if (isCorrect) {
      setScore(s => s + 10);
      setCorrectCount(c => c + 1);
    }
    
    setShowResult({ correct: isCorrect, answer: currentWord.word });
    
    setTimeout(() => {
      if (currentWordIndex < words.length - 1) {
        const nextIndex = currentWordIndex + 1;
        setCurrentWordIndex(nextIndex);
        setUserSpelling('');
        setShowResult(null);
        setTimeout(() => {
          speakWord(words[nextIndex]); // Pass the next word directly
          inputRef.current?.focus();
        }, 300);
      } else {
        endGame();
      }
    }, 2000);
  };

  const endGame = async () => {
    // Cancel any ongoing speech
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    setGameState('finished');
    
    const bonus = correctCount >= 10 ? 50 : correctCount >= 7 ? 30 : correctCount >= 5 ? 15 : 0;
    const totalPoints = score + bonus;
    
    try {
      await api.post('/games/complete', {
        game_id: 'spelling-bee',
        score: totalPoints,
        metadata: { correct_count: correctCount, total_words: words.length }
      });
      toast.success(`🎉 You earned ${totalPoints} points!`);
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  const skipWord = () => {
    // Cancel any ongoing speech
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    if (currentWordIndex < words.length - 1) {
      const nextIndex = currentWordIndex + 1;
      setCurrentWordIndex(nextIndex);
      setUserSpelling('');
      setShowResult(null);
      setTimeout(() => {
        speakWord(words[nextIndex]); // Pass the next word directly
        inputRef.current?.focus();
      }, 300);
    } else {
      endGame();
    }
  };

  return (
    <StudentLayout>
      <div data-testid="spelling-bee-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
        </div>

        {/* Game Header */}
        <div className="student-card bg-blue-100 border-blue-300 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Spelling Bee</h1>
                <p className="text-slate-600">
                  {gameState === 'playing' ? `Word ${currentWordIndex + 1} of ${words.length}` : 'Listen and spell!'}
                </p>
              </div>
            </div>
            {gameState === 'playing' && (
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-500" />
                <span className="font-heading text-xl font-bold text-slate-800">{score}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-blue-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">Spelling Bee</h2>
            <p className="text-lg text-slate-600 mb-8">
              Listen to each word and type the correct spelling.<br/>
              {words.length} words to spell!
            </p>
            <Button onClick={startGame} className="student-btn text-lg px-10 py-6">
              Start Spelling
              <Award className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && (
          <div className="student-card text-center">
            {/* Progress */}
            <div className="w-full bg-slate-200 rounded-full h-2 mb-8">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all"
                style={{ width: `${((currentWordIndex) / words.length) * 100}%` }}
              />
            </div>
            
            {/* Listen Button */}
            <Button
              onClick={speakWord}
              disabled={isPlaying}
              className={`w-24 h-24 rounded-full mb-6 ${isPlaying ? 'bg-blue-400' : 'bg-blue-500 hover:bg-blue-600'}`}
            >
              <Volume2 className={`w-12 h-12 text-white ${isPlaying ? 'animate-pulse' : ''}`} />
            </Button>
            
            <p className="text-slate-500 mb-2">Click to hear the word</p>
            <p className="text-slate-600 mb-6 italic">Hint: {currentWord?.hint}</p>
            
            {/* Result Display */}
            {showResult && (
              <div className={`mb-6 p-4 rounded-xl ${showResult.correct ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <div className="flex items-center justify-center gap-2">
                  {showResult.correct ? (
                    <Check className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <X className="w-6 h-6 text-red-600" />
                  )}
                  <span className={`font-bold ${showResult.correct ? 'text-emerald-700' : 'text-red-700'}`}>
                    {showResult.correct ? 'Correct!' : `The answer was: ${showResult.answer}`}
                  </span>
                </div>
              </div>
            )}
            
            {/* Input */}
            {!showResult && (
              <form onSubmit={handleSubmit} className="flex gap-4 justify-center items-center">
                <Input
                  ref={inputRef}
                  type="text"
                  value={userSpelling}
                  onChange={(e) => setUserSpelling(e.target.value)}
                  className="w-64 text-center text-2xl font-semibold h-14"
                  placeholder="Type here..."
                  autoFocus
                  autoComplete="off"
                  data-testid="spelling-input"
                />
                <Button type="submit" className="student-btn h-14 px-6">
                  Check
                </Button>
              </form>
            )}
            
            {!showResult && (
              <Button variant="ghost" onClick={skipWord} className="mt-4 text-slate-500">
                Skip this word
              </Button>
            )}
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">Well Done!</h2>
            <p className="text-xl text-slate-600 mb-4">
              You spelled {correctCount} out of {words.length} correctly!
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-600">
                +{score + (correctCount >= 10 ? 50 : correctCount >= 7 ? 30 : correctCount >= 5 ? 15 : 0)}
              </span>
              <span className="text-slate-600 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="student-btn">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default SpellingBeeGame;
